import { LoyaltyChallengeConfig } from '@shared/models';

import {
  BARCODE_FORMAT,
  DISPLAY_CONDITION,
  NavbarItemLocation,
  ProductCheckTabType,
  RewardFormat,
  ScanCheckMode,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const SHANE_CONFIG: LoyaltyChallengeConfig = {
  currency: 'GBP',
  currencyToRewardConversionRate: 1000,
  appIcon: 'assets/images/shane/favicon.ico',
  cssVariables: {
    'font-family': "'Roboto', sans-serif",
    'font-family-medium': "'Morrisons-Medium', sans-serif",
    'font-family-bold': 'Morrisons-Bold',

    'navbar-height': '60px',
    'navbar-background-color': 'white',

    'nav-link-color': '#666666',
    'nav-link-hover-color': '',
    'nav-link-active-color': '#008031',

    'nav-item-bg-color': '#e8e8e8',
    'nav-item-active-bg-color': 'white',
    'nav-item-font-family': 'var(--font-family)',
    'nav-item-hover-bg-color': 'rgba(0, 0, 0, 0.04)',

    'body-color': '#000000',
    'body-background-color': 'white',

    'challenge-primary-color': '#007531',

    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',

    // Onboarding welcome hero slot proportions (onboarding-hero.webp).
    'ob-hero-ratio': '705 / 317',

    // Solid backdrop of the immersive onboarding screens: Shane ships no
    // background artwork, so the green must come from here.
    'ob-surface': '#008031',
  },
  rewardFormat: RewardFormat.POINTS,
  og: {
    title: 'My Points Boosters | Morrisons More',
    description:
      'Earn more points with My Points Boosters. Shop and spend to earn points on your Morrisons More card. Select up to 10 offers and earn points on your favourite products.',
  },
  rewardTemplate:
    '<div>{points}</div><div style="font-size: 11px;line-height: 1; font-family: var(--font-family-medium)" class="points-label">Points</div>',
  pages: {
    availablePages: ['help', 'current', 'history'],
    help: {
      meta: {
        title: 'My Points Boosters | Morrisons More | Help',
        keywords:
          'Morrisons, More, Points Boosters, My Points Boosters, earn points, shop, spend, personalised offers, faq',
        description:
          'Earn more points with My Points Boosters. Shop and spend to earn points on your Morrisons More card. Select up to 10 offers and earn points on your favourite products.',
      },
      header: {
        background: '#00883E',
        bannerAlternativeText: '',
        goBackButton: {
          text: 'Go Back To My Points Boosters',
          backgroundColor: '#008031',
          color: 'white',
          fontFamily: 'var(--font-family)',
          borderRadius: '60px',
        },
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          type: TitleType.IMAGE,
          picture: {
            imageUrl: 'assets/images/shane/faq-title-image.webp',
            alternativeText: 'Frequently asked questions logo',
          },
        },
        questions: [
          {
            title: 'What are ‘My Points Boosters’?',
            content:
              'My Points Boosters are personalised product, category & basket spend offers which allow you to earn More Points when you shop & purchase qualifying products within the Booster. There is a combination of product/category & spend/frequency, all based on your 12 months transaction history. You will see 20 offers and will be able to activate up to 10 of these offers. The more you buy of the selected brands or categories throughout the My Points Booster period, the more points you can earn. All shops online and in-store will count towards your Booster. Excludes Amazon, Morrisons Daily Stores and Third Parties.',
          },
          {
            title: 'Where can I find ‘My Points Boosters’?',
            content:
              "You can find your Points Boosters by clicking the 'My Offers' section of the app or website, then clicking on the My Points Booster tile.  It will take you to a page, where you’ll see any seasonal or targeted promotions, as well as the My Points Boosters.",
          },
          {
            title: 'How can I check my progress?',
            content:
              "You can track your progress in the Morrisons More app or website , by selecting the ‘My Offers' section of the app/website, and tapping the ‘My Points Booster’ tile. Each offer you’ve selected will update with your progress as you take part & complete them. <br/><br/>If your progress has not updated after 4 days, please complete the enquiry form and one of the team will look into this for you.",
          },
          {
            title: 'When will I receive My Points Booster More Points?',
            content:
              "All of your points earnt during the Points Booster period will be added to your More account no later than 4 days after the Points Boosters have ended. Even if you complete your Booster early, your points will be added once the whole campaign has finished.<br/><br/> If after 4 days your points haven't been added, please complete the enquiry form and one of the team will look into this for you.",
          },
          {
            title: 'I shop online, can I take part?',
            content:
              'Yes, all of your shops within the My Points Booster period count, whether that is in-store or online. Please ensure your More Card is linked to your online account. For online purchases, your order must be delivered or collected on or before the last day of the booster period to count. Please remember that online orders can take up to 4 days to register spend on your Points Booster. Excludes Amazon, Morrisons Daily Stores and Third Parties.',
          },
          {
            title:
              'I shop both in-store and online - will my spend include both?',
            content:
              'Yes, as long as you scan your Morrisons More card when shopping in-store and your Morrisons More Card is linked to your online account we will combine your spend at Morrisons.<br/><br/> If you have waited 4 days (in-store purchases) and (online collection / delivery) and have purchased the correct product to qualify for the points and they are not showing in your Boosters, please complete the enquiry form and one of the team will look into this for you.',
          },
          {
            title: 'How long does it take for my spend to show in the app?',
            content:
              'It can take up to 4 days for your progress to be updated in the app for in-store purchases and in your delivery or collection for online purchases.<br/><br/>If you have waited the required period, have purchased the correct product to qualify for the points and they are not showing in your Boosters, please complete the enquiry form and one of the team will look into this for you.',
          },
          {
            title: 'Do I have to select 10 Boosters?',
            content:
              'No, you can select up to 10 My Points Boosters at a time. The more you select, the more rewards you could earn. The maximum number of My Points Boosters you can select at a time is 10.',
          },
          {
            title:
              'Can I change My Points Boosters after I have selected them?',
            content:
              "Yes. As long as you haven't started spending towards your Points Booster, you can deselect it and choose another one instead. Once you have made a purchase towards your Booster, it will remain active until you have either completed it or it has expired. You will only be able to select up to a maximum of 10 My Points Boosters.<br/><br/>If you require any assistance please complete the enquiry form and a member of the team will get in touch.",
          },
          {
            title: 'How are My Points Boosters determined?',
            content:
              'Your My Points Boosters are targeted to you based on your 12 months purchase history, to give you offers that are relevant and personalised to you - these could be things you already buy, or things we think you might like. There is a combination of product/category Points Booster & spend/frequency Points Boosters, all based on your 12 months transaction history.',
          },
          {
            title:
              'How do I know which products or spends count towards my goals?',
            content:
              'If the offer is a product/category offer, it will have a list of included products, which you can view by tapping the ‘eligible products’ button on each offer. You can search here by the product barcode to check if it is included. If you think you have bought a product which should count towards your spend, but your progress has not updated after 4 days, please complete the enquiry form and one of the team will look into it for you.',
          },
          {
            title:
              'I had a missing or substituted order on my online delivery or collection:',
            content:
              'Unfortunately if you have a missing item, or a substituted item that does not meet the qualifying parameters of the offer - it will not count towards your My Points Boosters spend.',
          },
          {
            title: 'Why am I not part of the promotion?',
            content:
              'This is a digital offer only for all app or web users. To participate, download the More App or login at <a target="_blank" href="https://www.morrisons.com/more"> Morrisons.com/More</a>. If you do not have a digital account, you can still benefit from all of our More Points offers and More Card Prices in-store and will continue to receive personalised offers relevant to you. Keep your eyes peeled, and keep scanning your Morrisons More Card so that future promotions can be tailored to you and your shopping habits.',
          },
          {
            title: 'What happens if I forget to scan my Morrisons More Card?',
            content:
              "Don’t worry, as long as you purchased qualifying items, you can still claim any missed spend towards your Booster. Simply use the “Forgot to Scan” feature in the 'More' tab of the navigation menu. You can do this by scanning or manually entering the barcode from your paper receipt within 14 days of your shopping trip.",
          },
          {
            title: 'Can I activate my offers after I shop?',
            content:
              'In order for your spend to count towards your Points Boosters, you will need to activate your chosen My Points Boosters before you shop. For online shops, you must activate before your order is delivered or collected. If you activate your offer after you shop, we will not be able to add your spend retrospectively.<br/><br/>If you require any further assistance with this query, please complete the enquiry form and one of the team will look into this for you.',
          },
        ],
        accordion: {
          styles: {
            title: {
              fontSize: '14px',
              padding: '10px 0',
              minWidth: '15rem',
            },
            description: {
              color: '#6b767c',
              fontSize: '12px',
              paddingTop: '0.5rem',
            },
          },
          alternativeTextExpandMore: 'Show more',
          alternativeTextExpandLess: 'Show less',
        },
      },
      extraLinks: [
        {
          title: {
            text: 'Need more help?',
            fontFamily: 'var(--font-family-bold)',
          },
          link: {
            color: 'white',
            label: 'Click here',
            fontFamily: 'var(--font-family)',
            backgroundColor: '#00883E',
            href: 'https://www.morrisons.com/help/?clkInTab=ContactUs',
          },
        },
      ],
    },
    currentChallenges: {
      meta: {
        title: 'My Points Boosters | Morrisons More',
        keywords:
          'Morrisons, More, Points Boosters, My Points Boosters, earn points, shop, spend, personalised offers',
        description:
          'Earn more points with My Points Boosters. Shop and spend to earn points on your Morrisons More card. Select up to 10 offers and earn points on your favourite products.',
      },
      creditDateNumber: 7,
      templateTopBanner: {
        mobile: [
          '<div style="text-align: center;padding: 5px;background-color: #FFCE00;display: flex;justify-content: center; align-items: center;">',
          '<img alt="Info icon about Points Boosters timeline." style="margin-right: 2px;" src="assets/images/shane/info_black.svg">',
          '<p style="font-size: 12px; margin-bottom: 0;font-family: Morrisons-Medium;color: black; padding: 0 10px;">Your points from the last campaign will be added to your account within 4 days of the Points Boosters ending.</p>',
          '</div>',
        ],
        tablet: [
          '<div style="text-align: center;padding: 5px;background-color: #FFCE00;display: flex;justify-content: center; align-items: center;">',
          '<img alt="Info icon about Points Boosters timeline." style="margin-right: 2px;" src="assets/images/shane/info_black.svg">',
          '<p style="font-size: 12px; margin-bottom: 0;font-family: Morrisons-Medium; color: black; padding: 0 10px;">Your points from the last campaign will be added to your account within 4 days of the Points Boosters ending.</p>',
          '</div>',
        ],
        desktop: [
          '<div style="text-align: center;padding: 5px;background-color: #FFCE00;display: flex;justify-content: center; align-items: center;">',
          '<img alt="Info icon about Points Boosters timeline." style="margin-right: 10px;" src="assets/images/shane/info_black.svg">',
          '<p style="font-size: 12px; margin-bottom: 0; font-family: Morrisons-Medium; color: black;">Your points from the last campaign will be added to your account within 4 days of the Points Boosters ending.</p>',
          '</div>',
        ],
      },
      templateBeforeChallenges: {
        mobile: [
          '<div style="max-width: 90vw;margin: 0px auto 25px;">',
          '<div style="display: flex;padding: 10px;background-color: #FAF9F6;align-items: center;border-radius: 5px;text-align: center;">',
          '<img alt="Information icon: Details about Points Boosters" src="assets/images/shane/info.svg" style="margin-right: 5px;">',
          '<p style="font-size: 11px; margin-bottom:0;">Please allow up to 4 days for your in-store and online shops to update and remember that spends applied to your Points Boosters are after More Card savings are deducted. More Points will be added to your account within 4 days of your Points Boosters ending.<br/> <b>Don’t forget to select your Points Boosters before you shop!</b></p>',
          '</div>',
          '</div>',
        ],
        tablet: [
          '<div style="max-width: 80vw;margin: 0px auto 25px;">',
          '<div style="display: flex;padding: 10px;background-color: #FAF9F6;align-items: center;border-radius: 5px;text-align: center;">',
          '<img alt="Information icon: Details about Points Boosters" src="assets/images/shane/info.svg" style="margin-right: 5px;">',
          '<p style="font-size: 12px; margin-bottom:0;">Please allow up to 4 days for your in-store and online shops to update and remember that spends applied to your Points Boosters are after More Card savings are deducted. More Points will be added to your account within 4 days of your Points Boosters ending.<br/> <b>Don’t forget to select your Points Boosters before you shop!</b></p>',
          '</div>',
          '</div>',
        ],
        desktop: [
          '<div style="max-width: 680px;margin: 0px auto 25px;">',
          '<div style="display: flex;padding: 10px;background-color: #FAF9F6;align-items: center;border-radius: 5px;text-align: center;">',
          '<img alt="Information icon: Details about Points Boosters" src="assets/images/shane/info.svg" style="margin-right: 5px;">',
          '<p style="font-size: 12px; margin-bottom:0;">Please allow up to 4 days for your in-store and online shops to update and remember that spends applied to your Points Boosters are after More Card savings are deducted. More Points will be added to your account within 4 days of your Points Boosters ending.<br/><b>Don’t forget to select your Points Boosters before you shop!</b></p>',
          '</div>',
          '</div>',
        ],
      },
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family)',
        backgroundColor: '#0DB14B',
        containerBackgroundColor: 'white',
        text: 'How does it work?',
        borderRadius: '60px',
        containerPadding: '2rem 0 0',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it works',
            color: '#171717',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: 'var(--challenge-primary-color)',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: 'white',
            fontFamily: 'var(--font-family)',
            backgroundColor: '#0DB14B',
            borderRadius: '60px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
      background: 'white',
      upButton: {
        content: '^',
        backgroundColor: '#FFCE00',
      },
      hintsBox: {
        backgroundColor: '#FFCE00',
        challengeNameSanitizers: [/Morrisons\s*/i, /,\s*/i],
        title: {
          color: '#000000',
          template:
            '<span><img src="assets/images/shane/global-hint-icon.webp" style="width: 15px;" alt="hint title icon"></span><span style="margin-top: 3.5px;margin-left: 5px;">SPECIAL TIP !</span>',
          fontFamily: 'var(--font-family-bold)',
        },
        content: {
          color: '#000000',
          fontFamily: 'var(--font-family)',
          template:
            '<span style="font-family: var(--font-family-bold);">Earn up to {variable} Points quickly </span> by reaching the first threshold of: {variable}.',
        },
        exitIcon: {
          alternativeText: 'Close hints box',
          url: 'assets/images/shane/hint-exit-btn.webp',
        },
      },
    },
    pastChallenges: {
      meta: {
        title: 'My Points Boosters History | Morrisons More',
        keywords:
          'Morrisons, More, Points Boosters, My Points Boosters, earn points, shop, spend, personalised offers',
        description:
          'Earn more points with My Points Boosters. Shop and spend to earn points on your Morrisons More card. Select up to 10 offers and earn points on your favourite products.',
      },
      banner: {
        visibility: true,
        title: 'My Points Boosters History',
        bannerAriaLabel: 'History page displaying past challenges',
        background: '#008031',
        styles: {
          title: {
            margin: 'unset',
            color: 'white',
            fontSize: '27px',
            fontFamily: 'var(--font-family-bold)',
            rotate: '-5deg',
            textAlign: 'center',
            width: '240px',
          },
          banner: {
            height: '130px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '2rem',
            justifyContent: 'center',
          },
        },
      },
      accordion: {
        variant: Variants.VARIANT_1,
        alternativeTextExpandMore: 'Show more',
        alternativeTextExpandLess: 'Show less',
        date: {
          color: '#272F35',
          format: 'MMMM d',
          fontFamily: 'var(--font-family)',
          template: '{firstDate} - {lastDate}',
        },
        reward: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
        },
      },
      noChallengesText: {
        color: '#272F35',
        fontFamily: 'var(--font-family)',
        text: 'No selected challenges',
      },
      noRewardsText: {
        color: '#272F35',
        fontFamily: 'var(--font-family)',
        text: 'No rewards earned',
      },
    },
    sorry: {
      background: 'white',
      content: [
        '<div style="max-width: 400px; margin: 0 auto 2rem;"><img src="assets/images/shane/logo.webp" alt="Morissons logo"></div>',
        '<div style="font-size: 1.5rem; text-align: center; color: #272f35; font-family: var(--font-family);">Points Boosters are not available at the moment</div>',
        '<div style="font-size: 1.5rem; text-align: center; color: #272f35; font-family: var(--font-family);">Please come again later</div>',
      ],
    },
  },
  components: {
    onboarding: {
      dialogLabel: 'Onboarding',
      closeLabel: 'Close onboarding',
      showHeaderPromise: true,
      welcome: {
        title: 'Earn even more on the product you love.',
        lead: 'Discover the challenges just picked for you.',
        startLabel: 'Discover my challenges',
        image: {
          imageUrl: 'assets/images/shane/onboarding-hero.webp',
          alternativeText: 'My Points Boosters to earn More Points',
        },
        stepsBadge: {
          // The leaf artwork is the badge here, so the disc behind it stays out
          // of the way.
          backgroundColor: 'transparent',
          textColor: '#232323',
          image: {
            imageUrl: 'assets/images/shane/onboarding-leaf.svg',
          },
        },
        steps: [
          {
            label: 'Shop',
            image: {
              imageUrl: 'assets/images/shane/onboarding-shop.webp',
              alternativeText: 'A Morrisons basket full of groceries',
            },
          },
          {
            label: 'Scan',
            sublabel: 'your card',
            image: {
              imageUrl: 'assets/images/shane/onboarding-scan.webp',
              alternativeText: 'Scanning a Morrisons More Card at the checkout',
            },
          },
          {
            label: 'Earn',
            sublabel: 'points',
            image: {
              imageUrl: 'assets/images/shane/onboarding-earn.webp',
              alternativeText: '£5 reward worth 500 More Card Points',
            },
          },
        ],
      },
      swipe: {
        title: 'Crush!',
        titleImage: {
          imageUrl: 'assets/images/shane/onboarding-crush.webp',
          alternativeText: 'Crush!',
        },
        subtitle: 'Select your favourite challenges!',
        hint: 'Swipe left to pass. Swipe right to get it.',
        passLabel: 'Pass',
        selectLabel: 'I love it',
        toast: {
          title: "It's a match!",
          body: 'Added to your challenges.',
        },
        counter: {
          selectedTemplate: '{n} / {max} selected',
          remainingTemplate: '{n} picks left',
          remainingSingularTemplate: '{n} pick left',
        },
        allChallengesLabel: 'See all challenges',
      },
    },
    header: {
      variant: Variants.VARIANT_5,
      backgroundColor: '#008031',
      bannerAriaLabel:
        'You can earn up to 20,000 points with the help of points boosters',
      date: {
        color: 'white',
        fontFamily: 'var(--font-family-medium)',
        label: 'Points Boosters valid until {variable}',
      },
      promiseTemplateBackgroundColor: '#ffcf03',
      promiseTemplate: [
        {
          color: '#171717',
          label: 'Earn up to',
          class: 'first-label',
          fontFamily: 'var(--font-family-bold)',
        },
        {
          label: '£20',
          color: '#171717',
          class: 'second-label',
          fontFamily: 'var(--font-family-bold)',
        },
        {
          color: '#171717',
          label: 'Worth of <br/> More Points <br/>every month',
          class: 'third-label',
          fontFamily: 'var(--font-family-bold)',
        },
      ],
      picture: {
        imageUrl: 'assets/images/shane/points-booster.webp',
        alternativeText: '',
      },
    },
    brands: {
      variant: Variants.VARIANT_1,
      brandsBgColor: 'white',
      border: '2px solid #ffce00',
      alternativeText: 'Logos of favourite brands',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      styles: {
        display: 'flex',
        padding: '20px 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        borderTop: '1px solid #f4f5f6',
      },
      linkStyles: {
        color: '#272f35',
        fontSize: '16px',
        fontFamily: 'var(--font-family)',
        textDecoration: 'none',
        lineHeight: '36px',
      },
      links: [
        {
          url: 'help',
          external: false,
          label: 'Help',
        },
      ],
    },
    tutorial: {
      background: '#00883E',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          dots: {
            activeBg: 'white',
            inactiveBg: '#00442A',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} on {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/shane/chevron-left.svg',
              alternativeText: 'Go back to the previous slide',
            },
            rightIcon: {
              alternativeText: 'Go to the next slide',
              imageUrl: 'assets/images/shane/chevron-right.svg',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_1,
          styles: {
            title: {
              color: '#272F35',
              fontFamily: 'var(--font-family-bold)',
            },
            description: {
              color: '#6B767C',
              fontFamily: 'var(--font-family)',
            },
            card: {
              border: 'none',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0px 4.269px 15.61px 0px rgba(38, 38, 38, 0.15)',
            },
          },
          steps: [
            {
              title: 'Select',
              indicatorImage: {
                alternativeText: 'Step 1',
                imageUrl: 'assets/images/shane/title-image-1.webp',
              },
              description:
                'Check out your personalised Points Boosters and select up to 10 to take part in',
              picture: {
                imageUrl: 'assets/images/shane/step-1.webp',
                alternativeText:
                  'Select up to {variable} personalised Points Boosters.',
              },
            },

            {
              title: 'Shop',
              indicatorImage: {
                alternativeText: 'Step 2',
                imageUrl: 'assets/images/shane/title-image-2.webp',
              },
              description:
                'Buy your favourite products to start earning More Points and track your progress in the app',
              picture: {
                imageUrl: 'assets/images/shane/step-2.webp',
                alternativeText:
                  'Purchase your favourite products to earn more and track progress in the app',
              },
            },
            {
              title: 'Earn',
              indicatorImage: {
                alternativeText: 'Step 3',
                imageUrl: 'assets/images/shane/title-image-3.webp',
              },
              description:
                'The More Points you earn will be added to your More Card within 4 days of the Points Boosters ending',
              picture: {
                imageUrl: 'assets/images/shane/step-3.webp',
                alternativeText: 'Earn up to £20 Worth of Points every month!',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_1,
      picture: {
        imageUrl: 'assets/images/shane/card.webp',
        alternativeText: 'Morrissons more card',
      },
      label: {
        color: '#272F35',
        text: 'My Points Boosters earned',
        fontFamily: 'var(--font-family-medium)',
      },
      amount: {
        color: '#272F35',
        fontFamily: 'var(--font-family-bold)',
      },
      tooltip: {
        visible: true,
        icon: 'assets/images/shane/info.svg',
        content:
          'Your points from the last campaign will be added to your account within 4 days of the Points Boosters ending',
        position: 'below',
        alternativeText: 'Click to read the message',
      },
      isOnlyReward: true,
    },
    navbar: {
      variant: Variants.VARIANT_1,
      menuVariant: Variants.VARIANT_2,
      hasLayoutPaddingTop: true,
      dropdownLabel: 'Menu',
      openButton: {
        imageUrl: 'assets/images/shane/menu.svg',
        buttonTitle: 'Go to help page',
        imageAlt: 'Go to help page',
      },
      fontFamily: 'var(--font-family)',
      logo: {
        url: 'current',
        height: '55px',
        imageUrl: 'assets/images/shane/logo.webp',
        alternativeText: 'Morissons logo',
      },
      items: [
        {
          url: 'current',
          label: 'My Points Boosters',
          ariaLabel: 'Go to my points boosters home page',
          borderTopOption: true,
          menuIcon: {
            imageUrl: 'assets/images/shane/menu-challenge-grey.svg',
            alternativeText: 'Home menu icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/shane/menu-challenge-green.svg',
            alternativeText: 'home menu icon selected',
          },
          displayOn: [NavbarItemLocation.DESKTOP, NavbarItemLocation.DROPDOWN],
        },
        {
          url: 'history',
          label: 'Past Points Boosters',
          ariaLabel: 'Go to my points history page.',
          borderTopOption: true,
          menuIcon: {
            imageUrl: 'assets/images/shane/menu-history-grey.svg',
            alternativeText: 'history menu icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/shane/menu-history-green.svg',
            alternativeText: 'history menu icon selected',
          },
          displayOn: [NavbarItemLocation.DESKTOP, NavbarItemLocation.DROPDOWN],
        },
        {
          url: 'help',
          label: 'FAQs',
          ariaLabel: 'Go to my points boosters help page',
          borderTopOption: true,
          menuIcon: {
            imageUrl: 'assets/images/shane/menu-help-grey.svg',
            alternativeText: 'help menu icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/shane/menu-help-green.svg',
            alternativeText: 'help menu icon selected',
          },
          displayOn: [NavbarItemLocation.DESKTOP, NavbarItemLocation.DROPDOWN],
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnNavbarWhen: [],
      showAfterCampaignSummaryWhen: [],
      showOnPages: 'all',
      showBeforeCampaignSummaryWhen: [],
      titleAppearing: ['row', 'row', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#272F35',
        fontFamily: 'var(--font-family)',
      },
      title: {
        color: '#272F35',
        showTitleOnMobile: true,
        label: 'Points Boosters end in',
        fontFamily: 'var(--font-family)',
      },
      numberStyle: {
        color: 'white',
        backgroundColor: '#0DB14B',
        fontFamily: 'var(--font-family-bold)',
      },
      texts: {
        days: 'Days',
        hours: 'Hours',
        minutes: 'Minutes',
        seconds: 'Seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_3,
          backgroundColor: '#11B14C',
          morissonPicture: {
            imageUrl: 'assets/images/shane/morissons-card.webp',
            alternativeText: 'Morissons Card',
          },
          amountTemplate:
            '<p class="amount" style="margin-bottom: 0; font-family: var(--font-family-bold); font-size: 28px;">{variable}</p><p class="reward-format" style="margin-bottom: 0; font-family: var(--font-family-medium); font-size: 22px;">Points</p>',
          completedPicture: {
            imageUrl: 'assets/images/shane/booster-completed.webp',
            alternativeText: 'You completed Booster',
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_1,
          catchPhrase: {
            amountBackgroundColor: '#0DB14B',
            imageUrl: 'assets/images/default/selection-rec.svg',
            alternativeText: 'Earn up to {variable} points',
            amountLabel: 'Earn up to',
            // 22px by default (1em of the 22px amount font); grows with the
            // OS font scale so boosted lines don't overlap (EAI-2167)
            lineHeight: 'max(22px, 1em)',
          },
          buttonCatchPhrase: {
            buttonAmountFontFamily: 'var(--font-family)',
            buttonAmountLabel: 'Select this Points Booster',
            buttonAmountBackgroundColor: '#0DB14B',
            borderRadius: '60px',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_2,
          text: 'Deactivate',
          title: 'Deactivate this challenge',
          color: '#DA2029',
          fontFamily: 'var(--font-family)',
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(15, 50, 80, 0.65)',
          borderRadius: '16px',
          banner: {
            border: '2px solid white',
            textColor: 'white',
            borderRadius: '2px',
            backgroundColor: '#DB3832',
            text: 'No more challenges',
            fontFamily: 'var(--font-family-bold)',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_2,
          logoAlternativeText: 'Logo banner',
          bannerAlternativeText: 'Challenges banner',
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            height: '143px',
            backgroundColor: '#FFFFFF',
          },
          subHeader: {
            color: 'black',
            fontSize: '12px',
            hasInfoButton: true,
            useDescription: false,
            backgroundColor: 'white',
            completedBackgroundColor: '#11B14C',
            fontFamily: 'var(--font-family)',
          },
          body: {
            progressbar: {
              spendLabel: 'Spend',
              labelColor: '#262626',
              containerColor: '#d4d4d4',
              progressColor: '#0DB14B',
              spendLabelTop: '25px',
            },
            rewards: {
              color: '#0DB14B',
              fontFamily: 'var(--font-family-bold)',
              defaultPath: 'assets/images/default/reward-rec.svg',
              bubbleHeight: '55px',
              alternativeText: {
                reached: 'I earned {variable} points',
                default: 'I earned {variable} points',
                unreached: 'Earn {variable} points',
              },
              backgroundColors: {
                reached: { fill: '#0DB14B', stroke: '#0DB14B' },
                unreached: { fill: 'white', stroke: '#0DB14B' },
                default: { fill: '#EAEAEA', stroke: '#EAEAEA' },
              },
            },
            targets: {
              height: '20px',
              color: '#262626',
              borderRadius: '5px',
              backgroundColor: '#FFCE00',
              targetUnReachedTitle: 'Earn £{variable}',
              onIcon: {
                imageUrl: 'assets/images/shane/on.svg',
                onIconAlternativeText: 'I earned £{variable}',
              },
              offIcon: {
                imageUrl: 'assets/images/shane/off.svg',
                offIconAlternativeText: 'I earned £{variable}',
              },
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: 'black',
            label: 'Total spent',
            fontFamily: 'var(--font-family)',
            spentAmount: {
              color: '#262626',
              borderRadius: '5px',
              backgroundColor: '#FFCE00',
              fontFamily: 'var(--font-family)',
            },
            productCheckButton: {
              ariaLabel: 'Click to check eligible products',
              color: '#00442A',
              label: 'Eligible products',
              textDecoration: 'underline',
              backgroundColor: 'transparent',
              fontFamily: 'var(--font-family)',
              icon: {
                imageUrl: 'assets/images/shane/lists.svg',
                alternativeText: 'Click to check eligible products',
              },
            },
          },
          hints: {
            color: '#000000',
            backgroundColor: '#FFCE00',
            fontFamily: 'var(--font-family-bold)',
            challengeBoxShadow: '0px 0px 25px 0px #FFCE00',
            template: 'Earn {variable} points! Only {variable} left to spend.',
            exitIcon: {
              alternativeText: 'Close hints box',
              imageUrl: 'assets/images/shane/hint-exit-btn.webp',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_3,
          footer: {
            remainingVisitsLabel: {
              color: '#00442A',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          header: {
            backgroundImage: {
              url: 'assets/images/shane/visit_banner.webp',
              bannerAriaLabel: 'Morissons Visits banner',
            },
            height: '140px',
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#11B14C',
            title: {
              color: 'black',
              fontFamily: 'var(--font-family)',
              label:
                'Minimum spend of just £{variable} per visit - 1 visit per day',
            },
          },
          body: {
            visitsLabel: {
              text: 'Shop {variable} times',
              color: '#ffcf03',
              fontFamily: 'var(--font-family-bold)',
            },
            rewardLabel: {
              text: 'Earn {variable} points',
              color: '#FFFFFF',
              fontFamily: 'var(--font-family-bold)',
            },
            firstLabel: {
              text: 'Visits',
              color: '#262626',
              fontFamily: 'var(--font-family-medium)',
            },
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/shane/1_on.svg',
              iconNonSelected: 'assets/images/shane/1_off.svg',
              iconSelectedAlternativeText: 'Number of times visited',
              iconNonSelectedAlternativeText: 'Number of times yet to visit',
            },
            totalBg: '#008031',
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_1,
      stackOnMobile: false,
      title: {
        color: '#0DB14B',
        text: 'My Points Boosters',
        fontFamily: 'var(--font-family-bold)',
        transform: 'rotate(-5.6deg)',
      },
      challengeLabels: {
        onGoingLabel: {
          color: '#333333',
          text: 'Selected',
          fontFamily: 'var(--font-family)',
        },
        completedLabel: {
          color: '#333333',
          text: 'Completed',
          fontFamily: 'var(--font-family)',
        },
      },
      counterBorderRadius: '5px',
      challengesInfos: {
        color: 'white',
        backgroundColor: '#0DB14B',
        fontFamily: 'var(--font-family-bold)',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '50px',
        backgroundColor: '#007531',
        fontFamily: 'var(--font-family)',
        ariaLabel: 'Close this popin',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          logo: {
            alternativeText: 'Logo Banner',
          },
          backgroundColor: '',
          backgroundImage: '',
        },
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: '',
          picture: {
            imageUrl: 'assets/images/shane/cancel.svg',
            alternativeText: 'Do you want to cancel this Booster?',
          },
        },
        body: {
          color: '#272f35',
          fontFamily: 'var(--font-family)',
          label:
            'If you change your mind, you can select this Points Booster again. Just remember that you’ll need to do this before you shop with us & buy any qualifying products.',
        },
        actionButtons: {
          variant: Variants.VARIANT_1,
          confirmButton: {
            label: 'Cancel',
            borderRadius: '50px',
            textColor: '#FFFFFF',
            backgroundColor: '#007531',
            fontFamily: 'var(--font-family)',
            ariaLabel: 'Do you want to cancel this challenge',
          },
          closeButton: {
            label: 'Keep it',
            textColor: '#007531',
            borderColor: '#007531', // if not, it will be the background color
            borderRadius: '50px',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family)',
            ariaLabel: 'Go back, and continue this challenge',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family)',
        tabs: [
          {
            type: ProductCheckTabType.SCAN,
            checkMode: ScanCheckMode.PRODUCT_LIST,
            label: 'Scan for a product',
            title: 'Barcode Verification',
            readers: [BARCODE_FORMAT.EAN_8, BARCODE_FORMAT.EAN_13],
            successMessage: 'Product eligible',
            errorMessage: 'Product not eligible',
            startButtonLabel: 'Start scanner',
            retryPermissionButtonLabel: 'Retry camera access',
            failureMessage: 'An error occurred, please try again',
            restartButtonLabel: 'Restart Scan',
            errorWhenCameraPermissionDenied:
              'Please allow camera access to use the scanner.',
            errorWhenCameraNotFound: 'Camera not available',
            errorWhenCameraBusy:
              'The camera is in use by another app. Close it, then try again.',
            previewLabel: 'Barcode scanner preview',
            subtitle: 'Scan your product here',
          },
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            failureMessage: 'An error occurred, please try again',
            label: 'Search for a product',
            searchable: true,
            inputPlaceholder: 'Enter a barcode to search',
            tableTitle: {
              text: 'List of eligible products',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Product',
                color: 'var(--challenge-primary-color)',
                fontFamily: 'var(--font-family)',
              },
              code: {
                header: 'Barcode',
                color: 'var(--challenge-primary-color)',
                fontFamily: 'var(--font-family)',
              },
            },
          },
        ],
        tabConfig: {
          activeColor: 'white',
          inactiveColor: 'black',
          inactiveBackground: 'white',
          fontFamily: 'var(--font-family)',
          inactiveBorderBottom: '2px solid #e6e6e6',
          activeBackground: 'var(--challenge-primary-color)',
          activeBorderBottom: '2px solid var(--challenge-primary-color)',
        },
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: '',
          fontFamily: '',
          label: '',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/shane/first-selection.svg',
            alternativeText: 'You have chosen your first Boosters',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family)',
          label: 'You can select {variable} more to take part in',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        title: {
          color: '',
          fontFamily: '',
          label: '',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/shane/last-selection.svg',
            alternativeText: 'You have chosen all {variable} of your Boosters',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family)',
          label: 'Buy your favourite products to start earning More Points',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        title: {
          color: '',
          fontFamily: '',
          label: '',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/shane/maximum-selection.svg',
            alternativeText: 'You have hit maximum number of Boosters',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family)',
          label: 'Congratulations! You have hit maximum number of Boosters',
        },
      },
      outOfStockIssue: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Selection limit reached',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'This challenge has reached the maximum number of participants. Please choose another challenge.',
        },
      },
      genericError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Error',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            "An error occurred, and the Booster couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Request timed out',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'Selecting the Booster took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'This campaign has ended and Boosters are no longer available for selection.',
        },
      },
    },
  },
};
